import Layout from "../../components/Layout";
import { Helmet } from "react-helmet-async";
import {
  FiTruck,
  FiMapPin,
  FiPackage,
  FiClipboard,
  FiTrendingUp,
  FiBarChart2,
  FiSettings,
  FiCheckCircle,
  FiGlobe,
} from "react-icons/fi";

const IndustryLogistics = () => {
  const trackCTA = () => {
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("event", "click", {
        event_category: "CTA",
        event_label: "Sign Up - Logistics Industry Page",
      });
    }
  };

  return (
    <Layout>
      <Helmet>
        <title>Logistics Industry | RedERP</title>
        <meta
          name="description"
          content="RedERP is built for logistics and delivery companies to streamline fleets, shipments, warehouses, and procurement."
        />
      </Helmet>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[rgb(84,13,14)] to-black text-white py-20">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-10">
          <div className="flex-1 text-left">
            <h1 className="text-4xl font-bold mb-4">ERP for Logistics & Transport</h1>
            <p className="mb-6 text-sm sm:text-base">
              Fleet tracking, delivery scheduling, warehouse stock, and order fulfillment—RedERP offers an all-in-one solution to power logistics operations.
            </p>
            <a
              href="https://app.rederp.pro/sign-up"
              target="_blank"
              rel="noopener noreferrer"
              onClick={trackCTA}
              className="inline-block bg-white text-primary font-semibold px-6 py-3 rounded-full hover:bg-gray-100"
            >
              Get Started Now
            </a>
          </div>
          <div className="flex-1 hidden md:flex justify-end">
            <img
              src="/assets/images/logistics-industry.png"
              alt="Logistics ERP"
              className="w-[400px] object-contain"
            />
          </div>
        </div>
      </section>

      {/* Modules Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto text-center mb-10">
          <h2 className="text-3xl font-bold text-primary mb-3">
            Modules Tailored for Logistics Success
          </h2>
          <p className="text-gray-600">
            From delivery planning to supplier management, RedERP supports the full logistics cycle with ease.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 text-left max-w-6xl mx-auto">
          {[
            {
              icon: <FiTruck size={24} />,
              title: "Fleet Management",
              desc: "Track vehicles, assign deliveries, monitor maintenance, and fuel logs.",
            },
            {
              icon: <FiMapPin size={24} />,
              title: "Route Optimization",
              desc: "Plan and visualize delivery routes with GPS data and traffic insights.",
            },
            {
              icon: <FiPackage size={24} />,
              title: "Shipment & Tracking",
              desc: "Monitor parcels, generate waybills, and offer real-time customer tracking.",
            },
            {
              icon: <FiClipboard size={24} />,
              title: "Warehouse & Inventory",
              desc: "Manage incoming and outgoing goods across one or more warehouses.",
            },
            {
              icon: <FiTrendingUp size={24} />,
              title: "Delivery Scheduling",
              desc: "Assign drivers, set dispatch windows, and control fulfillment workflows.",
            },
            {
              icon: <FiBarChart2 size={24} />,
              title: "Logistics KPIs & Reports",
              desc: "Track on-time rates, load utilization, delivery times, and fleet efficiency.",
            },
            {
              icon: <FiSettings size={24} />,
              title: "Custom Workflows",
              desc: "Adapt delivery confirmations, client sign-offs, and pickup instructions.",
            },
            {
              icon: <FiGlobe size={24} />,
              title: "Vendor & Supplier Management",
              desc: "Handle procurement, tenders, and delivery contracts in one place.",
            },
            {
              icon: <FiCheckCircle size={24} />,
              title: "Regulatory & Tax Compliance",
              desc: "Comply with transport laws, VAT, and international customs declarations.",
            },
          ].map((mod, i) => (
            <div
              key={i}
              className="p-6 bg-gray-50 border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-all"
            >
              <div className="flex items-center gap-3 text-primary mb-3">
                {mod.icon}
                <h3 className="font-bold">{mod.title}</h3>
              </div>
              <p className="text-sm text-gray-600">{mod.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-gradient-to-br from-[rgb(84,13,14)] to-black text-white">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-10 py-16">
          <div className="flex-1 text-left">
            <h3 className="text-3xl font-bold mb-4">Optimize Your Logistics Network with RedERP</h3>
            <p className="mb-6 text-sm sm:text-base">
              Start your 7-day free trial and discover how RedERP transforms your logistics operations.
            </p>
            <a
              href="https://app.rederp.pro/sign-up"
              target="_blank"
              rel="noopener noreferrer"
              onClick={trackCTA}
              className="inline-block bg-white text-primary font-semibold px-6 py-3 rounded-full hover:bg-gray-100"
            >
              Try Logistics Module
            </a>
          </div>
          <div className="flex-1 hidden md:flex justify-end">
            <img
              src="/assets/images/cta2.png"
              alt="CTA Illustration"
              className="w-[380px] object-contain"
            />
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default IndustryLogistics;
